import { useState } from 'react';
import { useTravelText } from '../app/TravelLocaleContext';
import { Link, useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { ApiError } from '@formosa/shared/api/createClient';
import PasswordField from './PasswordField';
import PhoneCountryFields from './PhoneCountryFields';
import { useRegisterConfig } from './useRegisterConfig';
import { useVerifyCodeCooldown } from './useVerifyCodeCooldown';
import './auth.css';

const MODE = { EMAIL: 'email', PHONE: 'phone' };
const PASSWORD_PATTERN = /^\S{6,12}$/;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTravelText();
  const { registerConfig } = useRegisterConfig();
  const cooldown = useVerifyCodeCooldown(60);
  const [mode, setMode] = useState(MODE.EMAIL);
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    email: '',
    phoneNumber: '',
    countryEnName: '',
    countryKey: '',
    phoneAreaCode: '',
    verifyCode: '',
    newPassword: '',
  });

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function updatePhoneCountry(patch) {
    setForm((prev) => ({
      ...prev,
      countryKey: patch.countryKey ?? prev.countryKey,
      countryEnName: patch.countryEnName ?? prev.countryEnName,
      phoneAreaCode: patch.phoneAreaCode ?? prev.phoneAreaCode,
    }));
  }

  async function sendVerifyCode() {
    setError('');
    setSuccess('');
    setSendingCode(true);
    try {
      if (mode === MODE.EMAIL) {
        if (!form.email.trim()) throw new ApiError('請輸入電子郵件');
        await memberApi.sendVerifyCodeEmail({
          scene: 'RESET_LOGIN_PASSWORD',
          email: form.email.trim(),
        });
      } else {
        if (!form.phoneNumber.trim()) throw new ApiError('請輸入手機號碼');
        await memberApi.sendVerifyCodePhone({
          scene: 'RESET_LOGIN_PASSWORD',
          countryEnName: form.countryEnName,
          phoneAreaCode: form.phoneAreaCode,
          phoneNumber: form.phoneNumber.trim(),
        });
      }
      setSuccess(mode === MODE.EMAIL ? t('travel.auth.emailVerificationAccepted') : '驗證碼已送出');
      cooldown.start();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '驗證碼送出失敗');
    } finally {
      setSendingCode(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (loading) return;
    setError('');
    setSuccess('');
    if (!PASSWORD_PATTERN.test(form.newPassword)) {
      setError('密碼須為 6–12 字，且不含空格');
      return;
    }
    setLoading(true);
    try {
      await memberApi.forgetPassword({
        resetType: 'LOGIN',
        newPassword: form.newPassword,
        verifyCode: form.verifyCode.trim(),
        email: mode === MODE.EMAIL ? form.email.trim() : undefined,
        phoneNumber: mode === MODE.PHONE ? form.phoneNumber.trim() : undefined,
        countryEnName: mode === MODE.PHONE ? form.countryEnName : undefined,
      });
      navigate('/login', {
        replace: true,
        state: { notice: '密碼已更新，可以用新密碼登入。' },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '重設密碼失敗');
    } finally {
      setLoading(false);
    }
  }

  if (!registerConfig.forgotPassword) {
    return (
      <main className="travel-auth">
        <div className="travel-auth__card">
          <Link className="travel-auth__brand" to="/explore">Formosa Travel</Link>
          <h1>忘記密碼</h1>
          <p>這個網站目前不能重設密碼。</p>
          <p className="travel-auth__footer"><Link to="/login">返回登入</Link></p>
        </div>
      </main>
    );
  }

  return (
    <main className="travel-auth">
      <form className="travel-auth__card" onSubmit={submit}>
        <Link className="travel-auth__brand" to="/explore">Formosa Travel</Link>
        <h1>忘記密碼</h1>
        <p>用電子郵件或手機重設登入密碼。</p>

        <div className="travel-auth__tabs" role="tablist" aria-label="重設方式">
          <button type="button" role="tab" aria-selected={mode === MODE.EMAIL}
            className={mode === MODE.EMAIL ? 'is-active' : ''}
            onClick={() => setMode(MODE.EMAIL)}>電子郵件</button>
          <button type="button" role="tab" aria-selected={mode === MODE.PHONE}
            className={mode === MODE.PHONE ? 'is-active' : ''}
            onClick={() => setMode(MODE.PHONE)}>手機</button>
        </div>

        {error ? <div className="travel-auth__error" role="alert">{error}</div> : null}
        {success ? <div className="travel-auth__success" role="status">{success}</div> : null}

        {mode === MODE.EMAIL ? (
          <label className="travel-auth__field">
            <span>電子郵件</span>
            <input required type="email" value={form.email}
              onChange={(e) => updateField('email', e.target.value)} disabled={loading} />
          </label>
        ) : (
          <>
            <PhoneCountryFields
              idPrefix="forgot-phone"
              countryKey={form.countryKey}
              countryEnName={form.countryEnName}
              phoneAreaCode={form.phoneAreaCode}
              onChange={updatePhoneCountry}
              allowedEnNames={registerConfig.enNames}
              disabled={loading || sendingCode}
            />
            <label className="travel-auth__field">
              <span>手機</span>
              <input required type="tel" value={form.phoneNumber}
                onChange={(e) => updateField('phoneNumber', e.target.value)} disabled={loading} />
            </label>
          </>
        )}

        <div className="travel-auth__code-block">
          <label className="travel-auth__field" htmlFor="travel-forgot-verify">
            <span>驗證碼</span>
            <input
              id="travel-forgot-verify"
              required
              value={form.verifyCode}
              onChange={(e) => updateField('verifyCode', e.target.value)}
              disabled={loading}
            />
          </label>
          <button
            type="button"
            className="travel-auth__secondary"
            onClick={sendVerifyCode}
            disabled={sendingCode || cooldown.active || loading}
          >
            {sendingCode
              ? '送出中…'
              : cooldown.active
                ? `請於 ${cooldown.remaining} 秒後重送`
                : '送出驗證碼'}
          </button>
        </div>

        <PasswordField
          label="新密碼（6–12 字，不含空格）"
          required
          minLength={6}
          maxLength={12}
          autoComplete="new-password"
          value={form.newPassword}
          onChange={(e) => updateField('newPassword', e.target.value)}
          disabled={loading}
        />

        <button type="submit" className="travel-auth__primary" disabled={loading}>
          {loading ? '更新中…' : '重設密碼'}
        </button>

        <p className="travel-auth__footer"><Link to="/login">返回登入</Link></p>
      </form>
    </main>
  );
}
